import type { GroupedAlert } from '@kirin/api-client/contracts/alerts';
import {
  ExtensionFindingType,
  type ScoreContributor,
} from '@kirin/api-client/contracts/analyses';
import { GITLEAKS_RULES_BY_ID } from '@kirin/api-client/data/gitleaks-rules';
import { getEntropyConfidence } from '@kirin/api-client/helpers/secret-confidence';
import {
  engineLabel,
  isProtectedDirRuleId,
} from '../AlertSecretDetails.helpers';
import {
  formatToolCallScore,
  formatToolCallScoreAgainstThreshold,
  formatToolCallThreshold,
} from '../tool-call-detail.helpers';

/**
 * KIR-4272 — "Detection Logic": the finding-specific reason detection fired.
 *
 * This module answers exactly one question: **why did THIS finding match?** It
 * deliberately does not answer:
 *
 * - *what was evaluated* — that is Evidence (the Location block, the
 *   "DETECTED ON CLIENT" panel, the OSV component section). KIR-4269.
 * - *why is it dangerous* — that is Risk Assessment (severity, CVSS). KIR-4270.
 * - *what do I do about it* — that is Remediation. KIR-4271.
 *
 * Every input is already on the wire (`groupedAlertSchema` /
 * `alertOccurrenceSchema`), so nothing here needs a migration or a backfill and
 * existing findings light up retroactively. Where a scanner never persisted a
 * per-finding reason, this resolver returns an explicit `unavailable` result —
 * it never substitutes type-level copy for the real reason, which is the whole
 * point of the ticket.
 *
 * Colocated with its only consumer on purpose. If Slack notifications ever want
 * the same text, move it to `@kirin/api-client` then.
 */

/** One renderable unit of detection logic. `blocks.tsx` switches on `kind`. */
export type DetectionBlock =
  /** Prose — a rule's authored reason, a classifier's summary. */
  | { kind: 'text'; label: string; value: string }
  /**
   * Verbatim mono content — a matched snippet, a codepoint, a config path.
   * `copyValue` carries the untruncated text when `value` is shortened for
   * display, so the copy button never hands back a clipped payload.
   */
  | { kind: 'code'; label: string; value: string; copyValue?: string }
  /**
   * KIR-4229: a window of scanned content with the flagged region marked.
   * Distinct from `code` because the value is not opaque — it carries markers
   * the renderer highlights.
   */
  | { kind: 'excerpt'; label: string; value: string }
  /**
   * A named rule, optionally with the raw pattern it matched on. `pattern` is
   * rendered as code; for the `regex`/`file-pattern` secret engines `ruleId`
   * *is* the raw regex (see `contracts/scans.ts`).
   */
  | { kind: 'rule'; label: string; ruleName: string; pattern?: string }
  /** Structured rows — classifier factors, config key/value pairs. */
  | { kind: 'kv'; label: string; rows: { k: string; v: string }[] }
  /** Weighted rule contributors that summed past the finding threshold. */
  | {
      kind: 'chips';
      label: string;
      score?: number;
      contributors: ScoreContributor[];
    }
  /** In-app action to the control that governs the finding. Route, not URL. */
  | { kind: 'link'; label: string; text: string; to: string };

/**
 * Why no detection logic is available. Both render as an honest unavailable
 * state — never as generic type-level copy dressed up as the real reason.
 *
 * - `not-recorded` — this detection does not persist a per-finding reason at
 *   all. A backend change is required (tracked separately per finding type).
 * - `legacy-finding` — this finding type *does* record a reason, but this row
 *   predates the field. There is nothing to backfill from.
 * - `gated` — the reason exists on the finding but the read path strips the
 *   field that carries it because another capability flag is off. Distinct from
 *   `legacy-finding` on purpose: "your org has this turned off" and "this row
 *   never had it" are different answers, and showing the wrong one sends an
 *   operator looking for a backend bug that isn't there.
 */
export type DetectionUnavailableReason =
  | 'not-recorded'
  | 'legacy-finding'
  | 'gated';

/**
 * Which flag-gated field groups actually reached the DTO. The alert read path
 * omits `sourceDetails` unless `alert_source_details` is on, and the secret
 * enrichment fields (`engine`/`ruleId`/`ruleName`/`entropy`) unless
 * `secret_detection_details` is on — see `resolveDtoSourceDetails` and the
 * `secretDetailsEnabled && isSecretFinding` guards in `alerts-helpers.ts`.
 * Detection Logic reads both groups, so it needs to know the difference between
 * "absent" and "withheld". Omitted means "assume present" (test fixtures and
 * any caller that already has full data).
 */
export interface DetectionLogicAvailability {
  sourceDetailsAvailable?: boolean;
  secretDetailsAvailable?: boolean;
  /**
   * Route to the Edit Secret Detection Rules page (KIR-4590). Supplied only
   * while
   * `gitleaks_rule_management` is on; omitted suppresses the Manage-rule
   * action. The resolver appends `?rule=<ruleId>`.
   */
  gitleaksRulesPath?: string;
}

export type DetectionLogicResult =
  | { available: true; blocks: DetectionBlock[] }
  | { available: false; because: DetectionUnavailableReason };

/** The alert fields this resolver reads. All already exist on the contract. */
export type DetectionLogicInput = Pick<
  GroupedAlert,
  | 'type'
  | 'description'
  | 'location'
  | 'sourceDetails'
  | 'scoreBreakdown'
  | 'engine'
  | 'ruleId'
  | 'ruleName'
  | 'entropy'
  | 'matchContext'
  | 'detectionPattern'
  | 'isAIGenerated'
  | 'toolCallScan'
>;

const UNAVAILABLE_NOT_RECORDED: DetectionLogicResult = {
  available: false,
  because: 'not-recorded',
};
const UNAVAILABLE_LEGACY: DetectionLogicResult = {
  available: false,
  because: 'legacy-finding',
};
const UNAVAILABLE_GATED: DetectionLogicResult = {
  available: false,
  because: 'gated',
};

/* -------------------------------------------------------------------------- */
/* Location parsing                                                           */
/* -------------------------------------------------------------------------- */

/**
 * Backend `location` values embed the matched span behind a labelled, quoted
 * prefix — `Command: "rm -rf /"`, `Text: "ghp_…"`, `Message: "<decoded>"`.
 * Curly quotes are accepted because some scanners emit them (mirrors
 * `ScanDetailFormattedText`).
 *
 * The closing quote is anchored to a line end so a quote *inside* the payload
 * does not terminate the match. That alone is not enough for `Message`: a
 * decoded steganography payload runs to several lines, and an interior line
 * that happens to end in a quote satisfied the lazy body early — truncating the
 * payload mid-sentence (`exfiltrate the "keys` instead of the whole message).
 * `Message` is always the last segment of a steganography `location`, so a
 * greedy body is safe there and takes the payload to its real final quote.
 *
 * `Command`/`Text` keep the lazy body deliberately: they are matched against
 * the same string in sequence, so a greedy body could run from one label
 * through a later label's closing quote and report the two as one span.
 */
function extractQuoted(
  location: string | null | undefined,
  key: 'Command' | 'Text' | 'Message'
): string | null {
  if (!location) return null;
  const body = key === 'Message' ? '[\\s\\S]*' : '[\\s\\S]*?';
  const match = location.match(
    new RegExp(`${key}:\\s*["“”](${body})["“”]\\s*$`, 'm')
  );
  const value = match?.[1]?.trim();
  return value != null && value.length > 0 ? value : null;
}

/**
 * The exact span a pattern caught, marked by the scanner.
 *
 * `boldMatchWithTrim`
 * (`apps/backend/src/policies/cursor-chat/formatting-utils.ts`) wraps the
 * matched substring in `**…**` and the caller embeds the WHOLE haystack in
 * `location` as `Command: "<text>"` / `Text: "<text>"`. The Location field on
 * the card already renders that haystack verbatim, so the caught span is the
 * one part of the payload Location does not already communicate — and repeating
 * the haystack here is pure duplication.
 *
 * Deliberately narrow on both sides:
 *  - `[^*]` for the body plus the `(?!\*)` guard, so a masking RUN can never
 *    parse as a span. `secret-leakage` emits `Text: "AKIA**********0001"`,
 *    where the asterisks are redaction, not emphasis.
 *  - only reached for the cursor-chat types that actually go through the
 *    bolding util (see {@link MATCHED_PATTERN_TYPES}).
 */
const BOLD_SPAN = /\*\*(?!\*)([^*]+?)\*\*/g;

/**
 * Upper bound on a candidate span, in characters — a real matched token is
 * short, and this rejects the runaway "spans" that phantom marker pairs make.
 */
const MAX_SPAN_LENGTH = 200;

/**
 * Upper bound on the text the finding's pattern is re-run over. The pattern is
 * Kirin's own curated rule, but the subject comes from a scanned file, and an
 * unbounded subject is how a backtracking pattern becomes a hang.
 */
const MAX_REPLAY_LENGTH = 4000;

/** Safety valve on a pathologically general pattern (`.` and friends). */
const MAX_SPANS = 20;

/**
 * Exactly the trailing trim `boldMatchWithTrim` applies before wrapping a match
 * (`apps/backend/src/policies/cursor-chat/formatting-utils.ts`). Kept
 * character-for-character in sync: a pattern like `eval\s*\(` matches
 * `eval (` but is BOLDED as `eval`, so any comparison that skips this trim
 * concludes the pattern does not match its own span.
 */
const BOLD_TRAILING_TRIM = /[\s(]+$/;

/**
 * Quote and delimiter characters a pattern can drag into its own match.
 *
 * Several command patterns end in an optional quote or path delimiter, so the
 * same rule firing on `rm -rf /"` and `rm -rf /'` produces spans that differ
 * only by a trailing artifact. Stripped from both ends before comparing, so
 * those collapse into one row instead of reading as separate catches.
 */
const SPAN_EDGE_NOISE = /^["'`\s]+|["'`\s;,]+$/g;

/**
 * The finding's own pattern, compiled the way the scanner compiles it.
 *
 * `createPatternRegex` uses `gi` — the `i` is load-bearing here: a pattern
 * written `RM\s+-RF` bolds a span of `rm -rf`, which a case-SENSITIVE compile
 * rejects. `g` is required by `matchAll`.
 *
 * Returns `null` rather than throwing when the pattern will not compile: an
 * RE2-only construct reaching a browser must degrade to "cannot re-derive", not
 * break the card.
 */
function compileDetectionPattern(pattern: string | null): RegExp | null {
  if (pattern == null) return null;
  try {
    return new RegExp(pattern, 'gi');
  } catch {
    return null;
  }
}

/**
 * Re-derive the caught spans by replaying the finding's pattern over the payload
 * — the same operation `getMatchedTextFromCommand` performed to produce them.
 *
 * This REPLACES verifying marker-derived candidates against the pattern, which
 * looked equivalent and is not: `boldMatchWithTrim` strips trailing whitespace
 * and `(` before wrapping, so `eval\s*\(` stores a span of `eval` and
 * `curl\s+` stores `curl` — spans their own pattern does not match. A verifier
 * therefore silently dropped the Exact match row for exactly those patterns.
 * Replaying the pattern and applying the same trim cannot drift that way,
 * because it is the same algorithm rather than an inverse of it.
 */
function replayPattern(original: string, pattern: RegExp): string[] {
  const spans: string[] = [];
  for (const match of original.matchAll(pattern)) {
    const span = match[0]?.replace(BOLD_TRAILING_TRIM, '');
    if (span == null || span.length === 0 || span.length > MAX_SPAN_LENGTH) {
      continue;
    }
    spans.push(span);
    if (spans.length >= MAX_SPANS) break;
  }
  return spans;
}

/**
 * Candidates read off the `**…**` markers. The fallback for rows with no usable
 * pattern.
 *
 * `**…**` alone does NOT identify a caught span: the markers are markdown the
 * scanner adds, but the payload is arbitrary scanned text that may carry its own
 * asterisks — Python `**kwargs`, an exponent, a markdown-formatted command.
 * `BOLD_SPAN` cannot tell those apart, so it pairs a closing marker from one
 * place with an opening marker from another and reports the unrelated text
 * between them. That put four bogus rows on a real alert (PR #2492).
 *
 * Only the cheap structural guards apply here, so a phantom can still slip
 * through. That is the honest limit of a row written before `detectionPattern`
 * was persisted — there is nothing to backfill from.
 */
function spansFromMarkers(payload: string): string[] {
  const spans: string[] = [];
  for (const match of payload.matchAll(BOLD_SPAN)) {
    const raw = match[1];
    // A genuine span is a token from one line, never a multi-line slab of a
    // command — that shape only comes from paired-up unrelated markers.
    if (raw == null || raw.length > MAX_SPAN_LENGTH || raw.includes('\n')) {
      continue;
    }
    spans.push(raw);
    if (spans.length >= MAX_SPANS) break;
  }
  return spans;
}

/**
 * Every DISTINCT span the finding's pattern caught, in payload order.
 *
 * Prefers replaying the pattern ({@link replayPattern}); falls back to the
 * markers ({@link spansFromMarkers}) when the pattern is absent, will not
 * compile, or the payload is too long to replay safely.
 *
 * Distinctness then follows in two passes:
 *  1. compare with edge noise stripped, so quote artifacts collapse;
 *  2. drop any span contained in another, keeping the longer one — `rm -rf` adds
 *     nothing next to `rm -rf /`, and the longer span is the more specific
 *     statement of what actually matched.
 *
 * Order follows first appearance. Containment is one-directional on length:
 * equal-length spans that differ are both kept, since neither refines the other.
 */
function caughtSpans(
  payload: string,
  detectionPattern: string | null
): string[] {
  const compiled = compileDetectionPattern(detectionPattern);
  // The scanner bolded a copy of this text; stripping the markers recovers the
  // string the pattern originally ran against.
  const original = payload.replaceAll('**', '');
  const candidates =
    compiled != null && original.length <= MAX_REPLAY_LENGTH
      ? replayPattern(original, compiled)
      : spansFromMarkers(payload);

  const seen: string[] = [];
  for (const candidate of candidates) {
    const span = candidate.replace(SPAN_EDGE_NOISE, '');
    if (span.length > 0 && !seen.includes(span)) seen.push(span);
  }
  return seen.filter(
    span =>
      !seen.some(other => other.length > span.length && other.includes(span))
  );
}

/**
 * `Why flagged:` evidence block the AI helpers append to `location` — the
 * classifier's top key factors plus its confidence. Kept in sync with
 * `apps/backend/src/domain/findings/ai-evidence-marker.ts`.
 */
const AI_EVIDENCE_MARKER = 'Why flagged:';
const CONFIDENCE_LINE = /^Confidence:\s*(.+)$/;

/** The classifier's key factors + confidence, parsed out of `location`. */
function parseAiEvidence(
  location: string | null | undefined
): { factors: string[]; confidence?: string } | null {
  if (!location) return null;
  const markerAt = location.indexOf(AI_EVIDENCE_MARKER);
  if (markerAt < 0) return null;
  const factors: string[] = [];
  let confidence: string | undefined;
  for (const raw of location
    .slice(markerAt + AI_EVIDENCE_MARKER.length)
    .split('\n')) {
    const line = raw.trim();
    if (line.startsWith('- ')) {
      const factor = line.slice(2).trim();
      if (factor.length > 0) factors.push(factor);
      continue;
    }
    const confidenceMatch = line.match(CONFIDENCE_LINE);
    if (confidenceMatch?.[1]) confidence = confidenceMatch[1].trim();
  }
  if (factors.length === 0 && confidence == null) return null;
  return { factors, ...(confidence ? { confidence } : {}) };
}

/**
 * `ai-detected-threat` joins its key factors with `, ` onto `location` rather
 * than using the `Why flagged:` block, so it needs its own split. Only treated
 * as factors when the value is not a labelled/quoted location.
 */
export function parseJoinedFactors(
  location: string | null | undefined
): string[] {
  if (!location) return [];
  if (/^(Command|Text|Message|File|Package|Tool):/.test(location.trim())) {
    return [];
  }
  return location
    .split(',')
    .map(part => part.trim())
    .filter(part => part.length > 0);
}

/** The `a.b.c | Mode: …` config-path form the YOLO scanner puts on `location`. */
function parseConfigPath(location: string | null | undefined): string | null {
  if (!location) return null;
  const path = location.split('|')[0]?.trim();
  // A dotted settings key, not a filesystem path or a labelled prefix.
  if (path == null || path.length === 0) return null;
  if (path.includes(':') || path.includes('/') || path.includes('\\')) {
    return null;
  }
  return path.includes('.') ? path : null;
}

function trimmed(value: string | null | undefined): string | null {
  const out = value?.trim();
  return out != null && out.length > 0 ? out : null;
}

/**
 * Display cap for a decoded steganographic payload, which runs to hundreds of
 * characters and turns the one-line row into a horizontal scroller. The head is
 * what identifies it — an injected instruction announces itself in its first
 * sentence — so the row shows that plus a length, and `copyValue` keeps the
 * payload whole.
 */
const MAX_DECODED_DISPLAY_CHARS = 180;

function decodedMessageBlock(decoded: string): DetectionBlock {
  const label = 'Decoded message';
  if (decoded.length <= MAX_DECODED_DISPLAY_CHARS) {
    return { kind: 'code', label, value: decoded };
  }
  return {
    kind: 'code',
    label,
    value: `${decoded.slice(0, MAX_DECODED_DISPLAY_CHARS)}… (${decoded.length} characters)`,
    copyValue: decoded,
  };
}

/**
 * The authorship suffix `cursor-chat/index.ts` appends to every chat finding's
 * description — `… (in AI-generated command)` / `… (in user code block)`.
 *
 * Stripped when the structured `isAIGenerated` boolean is available, because the
 * same fact then gets its own row. Leaving both would put one fact on the card
 * twice — exactly the duplication this section was cleaned up to avoid — and the
 * row is the better home: it is scannable, and it is the reason the severity was
 * escalated (`calculateSeverity`). Legacy rows have no boolean, so their prose
 * suffix is left alone rather than silently dropping the only copy of the fact.
 */
const AUTHORSHIP_SUFFIX =
  /\s*\(in (?:AI-generated|user) (?:command|code block)\)\s*$/;

/* -------------------------------------------------------------------------- */
/* Per-family resolvers                                                       */
/* -------------------------------------------------------------------------- */

/**
 * Types whose backend `description` IS the matched rule's reason, plus the
 * matched span on `location` behind a `Command:` / `Text:` prefix. The static
 * `TYPE_DISPLAY` copy used to swallow both.
 */
const MATCHED_PATTERN_TYPES: ReadonlySet<string> = new Set([
  'dangerous-command',
  'risky-code',
  'credential-exposure',
  'prompt-injection',
]);

/**
 * Types whose entire per-finding signal is the `description` string a rule,
 * blocklist entry, or heuristic authored. No structured payload exists, so the
 * honest rendering is that reason as prose.
 */
const AUTHORED_REASON_TYPES: ReadonlySet<string> = new Set([
  ExtensionFindingType.MaliciousExtension,
  ExtensionFindingType.UnverifiedPublisher,
  ExtensionFindingType.SuspiciousExtensionName,
  ExtensionFindingType.Typosquatting,
  ExtensionFindingType.MixedScriptAttack,
  ExtensionFindingType.CustomCapabilityRule,
  'ai-mcp-tool-threat',
  'api_error',
  'pii-detected',
  'multiple-pii',
  'bulk-pii',
]);

/**
 * Types whose `description` names the character, codepoint, or range that
 * matched — verbatim content, so it renders as code rather than prose.
 */
const CODEPOINT_TYPES: ReadonlySet<string> = new Set([
  'hidden_range',
  'non_printable',
  'hidden_character',
  'hidden-characters',
  ExtensionFindingType.HomographAttack,
]);

/** Classifier-backed types carrying a `Why flagged:` evidence block. */
const AI_EVIDENCE_TYPES: ReadonlySet<string> = new Set([
  'ai-instructions-threat',
  'hooks-config-threat',
  'command-file-threat',
]);

/** YOLO/auto-approve config findings: the config key is the whole signal. */
const CONFIG_FLAG_TYPES: ReadonlySet<string> = new Set([
  'auto_run',
  'auto_fix',
  'auto_apply',
  'agent_auto_fix',
  'global_auto_approve',
  'terminal_auto_approve',
  'terminal_auto_reply',
  'mcp_discovery',
  'mcp_auto_start',
  'auto_accept_delay',
  /*
    Windsurf's three `windsurf.autoExecutionPolicy` findings. Cursor/Copilot
    YOLO types come from the hardcoded backend scanner; Windsurf is
    declarative-only (`WINDSURF_YOLO` in `contracts/platform-registry.ts`), so
    it is easy to miss here. Omitting them made an identical config finding
    render the honest-unavailable state on Windsurf and the config key on
    Cursor. `resolveConfigFlag` is type-agnostic — it reads `location`/
    `description` — so these need no other handling.
  */
  'auto_execute_all',
  'auto_execute_shell',
  'auto_execute_allowlist',
]);

/** Vulnerable-dependency types. Identity + CVSS live in Evidence / Risk. */
const OSV_TYPES: ReadonlySet<string> = new Set([
  'vulnerability',
  ExtensionFindingType.OSVVulnerability,
]);

/**
 * Types that persist no per-finding reason whatsoever — the `description` is a
 * fixed string, so showing it would be showing type-level copy. Each needs its
 * own backend ticket; until then the honest answer is "not recorded".
 */
const NOT_RECORDED_TYPES: ReadonlySet<string> = new Set([
  'blocked_by_policy',
  'untrusted_mcp_server',
  'content-size-exceeded',
  // KIR-4482: the reason is "first seen in the org", not a per-finding rule.
  'new_mcp_server',
]);

/**
 * `secret-leakage` — the richest case. `engine` names the detector; for the
 * `regex` engine `ruleId` is the raw regex (unreadable on its own, which is why
 * `ruleName` exists); for `gitleaks` `ruleId` is already a readable id and there
 * is no pattern to show. `file-pattern` (a path match) has its own resolver.
 */
function resolveSecretLeakage(
  alert: DetectionLogicInput,
  availability: DetectionLogicAvailability
): DetectionLogicResult {
  const engine = trimmed(alert.engine);
  if (engine === 'file-pattern') return resolveSecretFilePath(alert);

  const ruleId = trimmed(alert.ruleId);
  const ruleName = trimmed(alert.ruleName);
  if (ruleId == null && ruleName == null) {
    return availability.secretDetailsAvailable === false
      ? UNAVAILABLE_GATED
      : UNAVAILABLE_LEGACY;
  }

  const isRawPattern = engine === 'regex';
  // KIR-4590: looked up from the PERSISTED `ruleId`, so group/source light up
  // on findings recorded before this shipped — the backend can only stamp
  // `ruleName` going forward.
  const catalogRule =
    engine === 'gitleaks' && ruleId != null
      ? GITLEAKS_RULES_BY_ID.get(ruleId)
      : undefined;

  const blocks: DetectionBlock[] = [
    {
      kind: 'rule',
      label: 'Matched rule',
      ruleName: ruleName ?? catalogRule?.name ?? ruleId ?? '',
      // A gitleaks rule id is NOT a pattern — it gets its own "Rule ID" row.
      ...(isRawPattern && ruleId != null && ruleId !== ruleName
        ? { pattern: ruleId }
        : {}),
    },
  ];
  if (engine != null) {
    blocks.push({
      kind: 'kv',
      label: 'Detection engine',
      rows: [
        { k: 'Engine', v: engineLabel(engine) },
        // The toggle key on the rules screen, now that a name replaced it above.
        ...(catalogRule != null ? [{ k: 'Rule ID', v: catalogRule.id }] : []),
        { k: 'Source', v: catalogRule?.source ?? ruleSourceLabel(engine) },
        ...(catalogRule != null ? [{ k: 'Group', v: catalogRule.group }] : []),
        // Absent for the regex engines, where a 0 would read as "no
        // confidence" rather than "n/a". Banded with the SAME helper the
        // confidence badge uses, so the two can never disagree.
        ...(typeof alert.entropy === 'number'
          ? [
              {
                k: 'Confidence',
                v: CONFIDENCE_LABEL[getEntropyConfidence(alert.entropy)],
              },
              { k: 'Entropy', v: alert.entropy.toFixed(2) },
            ]
          : []),
      ],
    });
  }

  // A LINK, not an inline disable: turning a rule off goes through the rules
  // screen's Save flow so it lands in Audit Logs (KIR-4396).
  if (catalogRule != null && availability.gitleaksRulesPath != null) {
    blocks.push({
      kind: 'link',
      label: 'Manage rule',
      text: 'Manage rule in Detection Policies',
      to: `${availability.gitleaksRulesPath}?rule=${encodeURIComponent(catalogRule.id)}`,
    });
  }
  return { available: true, blocks };
}

// Producer prose for a path match. Frozen by `finding_key`, so safe to parse
// on findings sent without a `ruleId`.
const SECRET_FILE_DESCRIPTION_PREFIX = 'Secret File Detected - ';
const PROTECTED_DIR_DESCRIPTION_PREFIX =
  'Sensitive Directory Access - File is in protected directory: ';

const SECRET_FILE_RULE_LABEL = 'Matched secret-file rule';
const PROTECTED_DIR_LABEL = 'Matched protected directory';

/** KIR-4974 AC4: a path match never opens the file. */
const PATH_MATCH_NOTE: DetectionBlock = {
  kind: 'text',
  label: 'About this match',
  value:
    "Flagged because the file's path matched a secret-file rule or protected directory. This is a path-based match: it does not confirm that the file contains a secret, that the file was read, or that its contents reached the AI.",
};

/** The name after `prefix` in the producer's description, if any. */
function nameFromDescription(
  description: string | null,
  prefix: string
): string | null {
  if (!description?.startsWith(prefix)) return null;
  return description.slice(prefix.length).trim() || null;
}

/** The matched rule or directory recovered from the producer's description. */
function pathMatchFromDescription(
  description: string | null
): DetectionBlock | null {
  for (const [prefix, label] of [
    [PROTECTED_DIR_DESCRIPTION_PREFIX, PROTECTED_DIR_LABEL],
    [SECRET_FILE_DESCRIPTION_PREFIX, SECRET_FILE_RULE_LABEL],
  ] as const) {
    const ruleName = nameFromDescription(description, prefix);
    if (ruleName != null) return { kind: 'rule', label, ruleName };
  }
  return null;
}

/**
 * KIR-4974: `file-pattern` (path) matches. Always available, and never promises
 * a reason on future alerts.
 */
function resolveSecretFilePath(
  alert: DetectionLogicInput
): DetectionLogicResult {
  const ruleId = trimmed(alert.ruleId);
  const ruleName = trimmed(alert.ruleName);
  const description = trimmed(alert.description);

  let match: DetectionBlock | null;
  if (isProtectedDirRuleId(ruleId)) {
    match = {
      kind: 'rule',
      label: PROTECTED_DIR_LABEL,
      ruleName: ruleName ?? ruleId,
    };
  } else if (ruleId != null || ruleName != null) {
    // Unindexed patterns (e.g. agent defaults) arrive unnamed; the description
    // still carries the readable name.
    const name =
      ruleName ??
      nameFromDescription(description, SECRET_FILE_DESCRIPTION_PREFIX);
    match = {
      kind: 'rule',
      label: SECRET_FILE_RULE_LABEL,
      ruleName: name ?? ruleId ?? '',
      // Pattern row only when a separate name exists.
      ...(ruleId != null && name != null && ruleId !== name
        ? { pattern: ruleId }
        : {}),
    };
  } else {
    match = pathMatchFromDescription(description);
  }

  return {
    available: true,
    blocks: [
      match ?? {
        kind: 'text',
        label: 'Matched rule',
        value:
          'The secret-file rule or protected directory that matched was not recorded for this alert.',
      },
      {
        kind: 'kv',
        label: 'Detection engine',
        rows: [
          { k: 'Engine', v: engineLabel('file-pattern') },
          { k: 'Source', v: ruleSourceLabel('file-pattern') },
        ],
      },
      PATH_MATCH_NOTE,
    ],
  };
}

/** Band names for the confidence row. Matches the confidence badge's copy. */
const CONFIDENCE_LABEL: Record<
  ReturnType<typeof getEntropyConfidence>,
  string
> = {
  low: 'Low',
  medium: 'Medium',
  high: 'High',
};

/**
 * Ruleset a secret finding came from, for rules with no catalog entry. Mirrors
 * the engine split the backend stamps as `subPolicyKey`.
 */
function ruleSourceLabel(engine: string): string {
  if (engine === 'gitleaks') return 'Built-in secret detection ruleset';
  if (engine === 'regex') return 'Kirin custom secret rule';
  if (engine === 'file-pattern') return 'Kirin secret file rule';
  return engine;
}

/**
 * MCP `suspicious_description` — the rule that fired plus the literal span it
 * matched. The surrounding context window is NOT repeated here: the Location
 * block already renders `before <mark>match</mark> after` (KIR-4175), and
 * duplicating Evidence is explicitly out of bounds.
 */
function resolveSuspiciousDescription(
  alert: DetectionLogicInput,
  availability: DetectionLogicAvailability
): DetectionLogicResult {
  const mcp = alert.sourceDetails?.kind === 'mcp' ? alert.sourceDetails : null;
  const matchedRule = trimmed(mcp?.matchedRule) ?? trimmed(alert.description);
  if (matchedRule == null) {
    return availability.sourceDetailsAvailable === false
      ? UNAVAILABLE_GATED
      : UNAVAILABLE_LEGACY;
  }
  /*
    The matched span is NOT repeated. `getMcpLocationEvidence` renders the
    Location as `before <mark>match</mark> after` under exactly the same
    condition this resolver has the span at all — `sourceDetails.kind === 'mcp'`
    with a non-null `matchedText` — so whenever it could be shown here, Location
    is already showing it, highlighted, in its surrounding context. That is the
    better presentation of the two, and printing it twice is the duplication
    this section exists to avoid. The rule NAME is the finding-specific fact
    Location genuinely cannot carry.
  */
  return {
    available: true,
    blocks: [{ kind: 'rule', label: 'Matched rule', ruleName: matchedRule }],
  };
}

/**
 * Weighted extension findings — the rule ids and weights that summed past the
 * threshold. Absorbs the former standalone `WhyThisFired` section, including
 * its gating: custom capability rules route by severity (they carry an authored
 * reason instead), and a contributor list with no score is only meaningful for
 * a single weight-100 instant signal.
 */
function resolveWeightedExtension(
  alert: DetectionLogicInput
): DetectionLogicResult {
  const contributors = alert.scoreBreakdown?.contributors ?? [];
  const score = alert.scoreBreakdown?.score;
  const isInstantSignal =
    score === undefined &&
    contributors.length === 1 &&
    contributors[0]?.weight === 100;

  if (contributors.length === 0 || (score === undefined && !isInstantSignal)) {
    // Legacy heuristic findings persisted no ruleId/weights at all
    // (`custom_attributes` is NULL), so there is nothing to reconstruct.
    return UNAVAILABLE_LEGACY;
  }
  return {
    available: true,
    blocks: [
      {
        kind: 'chips',
        label: 'Contributing rules',
        ...(score !== undefined ? { score } : {}),
        contributors,
      },
    ],
  };
}

/**
 * Risky-extension findings, whose per-finding reason reaches the alert only via
 * KIR-4272's `sourceDetails.detectionReason`. Rows written before that field
 * existed carry nothing — the reason lived on the untyped `metadata`, which was
 * dropped at ingest, so there is no backfill source.
 */
function resolveRiskyExtension(
  alert: DetectionLogicInput,
  availability: DetectionLogicAvailability
): DetectionLogicResult {
  const reason =
    alert.sourceDetails?.kind === 'extension'
      ? trimmed(alert.sourceDetails.detectionReason)
      : null;
  if (reason == null) {
    // `sourceDetails` is the only carrier for this reason, and the read path
    // withholds the whole object when `alert_source_details` is off — so this
    // section needs BOTH flags on. Say so instead of claiming the row is old.
    return availability.sourceDetailsAvailable === false
      ? UNAVAILABLE_GATED
      : UNAVAILABLE_LEGACY;
  }
  return {
    available: true,
    blocks: [{ kind: 'text', label: 'Detection reason', value: reason }],
  };
}

/**
 * The stem the AI helpers write when the classifier returns no summary
 * (`kirin-ai/rules-helper.ts`, `kirin-ai-helper.ts` — the hooks-config and
 * command-file variants append "in hooks config" / "in command file" to the
 * same prefix). It is a placeholder, not reasoning: presenting it as the
 * detection logic is exactly the generic-copy problem this section exists to
 * fix, so it is filtered out rather than shown. The former `detected` carve-out
 * skipped it for the same reason (KIR-4214).
 */
const AI_FALLBACK_SUMMARY_STEM = 'AI detected potential security concerns';

/** The classifier's summary, or null when it is the no-summary placeholder. */
function classifierSummary(
  description: string | null | undefined
): string | null {
  const summary = trimmed(description);
  if (summary == null || summary.startsWith(AI_FALLBACK_SUMMARY_STEM)) {
    return null;
  }
  return summary;
}

/** Classifier findings with a `Why flagged:` factor list on `location`. */
function resolveAiEvidence(alert: DetectionLogicInput): DetectionLogicResult {
  const summary = classifierSummary(alert.description);
  const evidence = parseAiEvidence(alert.location);
  const blocks: DetectionBlock[] = [];
  if (summary != null) {
    blocks.push({
      kind: 'text',
      label: 'Classifier reasoning',
      value: summary,
    });
  }
  if (evidence != null) {
    const rows = evidence.factors.map((factor, index) => ({
      k: `Factor ${index + 1}`,
      v: factor,
    }));
    if (evidence.confidence) {
      rows.push({ k: 'Confidence', v: evidence.confidence });
    }
    if (rows.length > 0) {
      blocks.push({ kind: 'kv', label: 'Key factors', rows });
    }
  }
  return blocks.length > 0
    ? { available: true, blocks }
    : UNAVAILABLE_NOT_RECORDED;
}

/** `ai-detected-threat`: summary + comma-joined key factors on `location`. */
function resolveAiDetectedThreat(
  alert: DetectionLogicInput
): DetectionLogicResult {
  const summary = classifierSummary(alert.description);
  const factors = parseJoinedFactors(alert.location);
  // A placeholder summary is not reasoning, but the key factors beside it still
  // are — drop only the placeholder, and fall through to unavailable when the
  // factors are missing too.
  if (summary == null && factors.length === 0) return UNAVAILABLE_NOT_RECORDED;
  return {
    available: true,
    blocks: [
      ...(summary != null
        ? [
            {
              kind: 'text' as const,
              label: 'Classifier reasoning',
              value: summary,
            },
          ]
        : []),
      ...(factors.length > 0
        ? [
            {
              kind: 'kv' as const,
              label: 'Key factors',
              rows: factors.map((factor, index) => ({
                k: `Factor ${index + 1}`,
                v: factor,
              })),
            },
          ]
        : []),
    ],
  };
}

/**
 * `extensions_code_scan` — the YARA rule name and description, which the
 * scanner packs into `description` ahead of the `Match in:` / `Code:` sections.
 * Only the rule identity belongs here; the snippet itself is Evidence.
 */
function resolveCodeScan(alert: DetectionLogicInput): DetectionLogicResult {
  const description = trimmed(alert.description);
  if (description == null) return UNAVAILABLE_LEGACY;
  // `<ruleName>: <ruleDesc>` — plus, on findings recorded before KIR-4230
  // moved the evidence onto structured fields, a `\n\nMatch in: …\n\nCode:`
  // tail. Splitting on the blank line handles both.
  const head = description.split(/\n\n/)[0]?.trim() ?? description;
  const separator = head.indexOf(': ');
  const parsedRuleName = separator > 0 ? head.slice(0, separator).trim() : head;
  const ruleDescription =
    separator > 0 ? head.slice(separator + 2).trim() : null;

  // KIR-4230: prefer the structured rule identity, which also names the rule
  // string that fired (`kirin_cred_rule ($aws_key)`) — the description can
  // only ever carry the rule name. Falls back to the parse so pre-KIR-4230
  // findings keep resolving.
  const structuredRule =
    alert.sourceDetails?.kind === 'extension'
      ? trimmed(alert.sourceDetails.matchedRule)
      : null;
  const matchedLine =
    alert.sourceDetails?.kind === 'extension'
      ? alert.sourceDetails.matchedLine
      : undefined;

  return {
    available: true,
    blocks: [
      {
        kind: 'rule',
        label: 'Matched rule',
        ruleName: structuredRule ?? parsedRuleName,
      },
      ...(ruleDescription
        ? [
            {
              kind: 'text' as const,
              label: 'Rule description',
              value: ruleDescription,
            },
          ]
        : []),
      ...(matchedLine != null
        ? [
            {
              kind: 'kv' as const,
              label: 'Matched location',
              rows: [{ k: 'Line', v: String(matchedLine) }],
            },
          ]
        : []),
    ],
  };
}

/**
 * A rule reason plus the exact span the rule caught.
 *
 * It shows the CAUGHT SPAN, never the surrounding command or chat text: the
 * Location field already renders that payload in full, so echoing it back put
 * the same string on the card twice while answering nothing. What Location
 * cannot say is *which part of it* fired — a 300-character heredoc whose only
 * problem is one `eval` reads as entirely suspicious until the span is named.
 *
 * Three outcomes, and the omissions are the point:
 *  - several distinct spans → one `kv` row each, so a command that trips two
 *    patterns says so;
 *  - one span that is a strict subset of the payload → that span alone;
 *  - one span covering the whole payload, or no span at all → NOTHING. Location
 *    is then already showing exactly this text (`Text: "**DROP BASS**"`), and a
 *    second copy is noise. The reason row still renders, because the rule's
 *    description is not in `location` at all.
 */
function resolveMatchedPattern(
  alert: DetectionLogicInput
): DetectionLogicResult {
  const raw = trimmed(alert.description);
  if (raw == null) return UNAVAILABLE_LEGACY;
  // Only strip the prose suffix when the structured field can replace it.
  const reason =
    alert.isAIGenerated === undefined
      ? raw
      : (trimmed(raw.replace(AUTHORSHIP_SUFFIX, '')) ?? raw);
  const blocks: DetectionBlock[] = [
    { kind: 'text', label: 'Matched pattern', value: reason },
  ];

  const payload =
    extractQuoted(alert.location, 'Command') ??
    extractQuoted(alert.location, 'Text');
  if (payload != null) {
    const spans = caughtSpans(payload, trimmed(alert.detectionPattern));
    // Compared with the markers stripped: `**DROP BASS**` and `DROP BASS` are
    // the same text to a reader, and the whole point is to catch that case.
    const whole = payload.replaceAll('**', '').trim();
    if (spans.length > 1) {
      blocks.push({
        kind: 'kv',
        label: 'Exact match',
        rows: spans.map((span, index) => ({
          k: `Match ${index + 1}`,
          v: span,
        })),
      });
    } else if (spans.length === 1 && spans[0] !== whole) {
      blocks.push({ kind: 'code', label: 'Exact match', value: spans[0] });
    }
  }

  /*
    KIR-4272: the literal pattern that fired. Rendered verbatim and copyable — a
    regex retyped by hand is a different regex — and placed after the match so
    the card reads reason, then what was caught, then the rule that caught it.
    This is the row that makes a suspected false positive actionable without
    reading the scanner source.
  */
  const pattern = trimmed(alert.detectionPattern);
  if (pattern != null) {
    blocks.push({ kind: 'code', label: 'Pattern', value: pattern });
  }
  if (alert.isAIGenerated !== undefined) {
    blocks.push({
      kind: 'kv',
      label: 'Authorship',
      rows: [
        {
          k: 'Written by',
          v: alert.isAIGenerated ? 'AI agent' : 'User',
        },
      ],
    });
  }

  return { available: true, blocks };
}

/**
 * Config-flag findings. The setting key is the only per-finding datum that
 * survives — the detected value is dropped by the scanner for most of these, so
 * the description (which sometimes interpolates it) carries what is left.
 */
function resolveConfigFlag(alert: DetectionLogicInput): DetectionLogicResult {
  const path = parseConfigPath(alert.location);
  const reason = trimmed(alert.description);
  if (path == null && reason == null) return UNAVAILABLE_NOT_RECORDED;
  return {
    available: true,
    blocks: [
      ...(reason != null
        ? [{ kind: 'text' as const, label: 'Detected setting', value: reason }]
        : []),
      ...(path != null
        ? [{ kind: 'code' as const, label: 'Configuration key', value: path }]
        : []),
    ],
  };
}

/**
 * Vulnerable-dependency findings. The matching basis is a version-range hit in
 * an OSV advisory; the package identity, advisory id, and CVSS are Evidence and
 * Risk Assessment respectively and already render in their own sections, so
 * this block states the basis without repeating those rows.
 */
function resolveOsv(
  alert: DetectionLogicInput,
  availability: DetectionLogicAvailability
): DetectionLogicResult {
  const osvComponent =
    alert.sourceDetails?.kind === 'extension' ||
    alert.sourceDetails?.kind === 'mcp'
      ? alert.sourceDetails.osvComponent
      : undefined;
  const osvId =
    alert.sourceDetails?.kind === 'extension' ||
    alert.sourceDetails?.kind === 'mcp'
      ? trimmed(alert.sourceDetails.osvId)
      : null;
  if (osvComponent == null && osvId == null) {
    return availability.sourceDetailsAvailable === false
      ? UNAVAILABLE_GATED
      : UNAVAILABLE_LEGACY;
  }
  const name = trimmed(osvComponent?.name);
  const version = trimmed(osvComponent?.version);
  const subject =
    name != null && version != null ? `${name}@${version}` : (name ?? null);
  return {
    available: true,
    blocks: [
      {
        kind: 'text',
        label: 'Matched by',
        value:
          subject != null
            ? `Vulnerability database lookup: the installed ${subject} matches a version affected by ${osvId ?? 'a published advisory'}.`
            : `Vulnerability database lookup: the installed version matches a version affected by ${osvId ?? 'a published advisory'}.`,
      },
    ],
  };
}

/**
 * KIR-4836: tool-call scores render to 2 decimals (was 3) -- except where that
 * rounding would misstate the comparison this block exists to show. The live
 * cutoff is `0.496`, which 2 decimals prints as `0.50`, a threshold the backend
 * never used, and a score of `0.4955` prints the same way while having been
 * allowed. Both formatters are shared with the grouped-cards panel so the two
 * layouts cannot disagree about the same alert (PR #2685 review).
 */
function formatMalScore(value: number, threshold: number | undefined): string {
  return threshold === undefined
    ? formatToolCallScore(value)
    : formatToolCallScoreAgainstThreshold(value, threshold);
}

function resolveToolCallScan(alert: DetectionLogicInput): DetectionLogicResult {
  const blocks: DetectionBlock[] = [];
  const reason = trimmed(alert.description);
  if (reason != null) {
    blocks.push({ kind: 'text', label: 'Detection reason', value: reason });
  }

  const evidence = alert.toolCallScan;
  const threshold = evidence?.threshold;
  const labels = evidence?.labels;
  if (labels != null && Object.keys(labels).length > 0) {
    const flagged = evidence?.topThreatLabel;
    const rows = Object.entries(labels)
      .sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]))
      .map(([label, score]) => ({
        k: flagged != null && label === flagged ? `${label} (flagged)` : label,
        v: formatMalScore(score, threshold),
      }));
    blocks.push({ kind: 'kv', label: 'Class scores', rows });
  }

  const verdictRows: { k: string; v: string }[] = [];
  if (evidence?.malScore !== undefined) {
    verdictRows.push({
      k: 'Malicious score',
      v: formatMalScore(evidence.malScore, threshold),
    });
  }
  if (threshold !== undefined) {
    verdictRows.push({
      k: 'Threshold',
      v: formatToolCallThreshold(threshold),
    });
  }
  if (verdictRows.length > 0) {
    blocks.push({ kind: 'kv', label: 'Verdict', rows: verdictRows });
  }

  const contextRows: { k: string; v: string }[] = [];
  if (evidence?.sensitivePath !== undefined) {
    contextRows.push({
      k: 'Sensitive path',
      v: evidence.sensitivePath ? 'Yes' : 'No',
    });
  }
  if (evidence?.egressPrior !== undefined) {
    contextRows.push({
      k: 'Prior egress',
      v: evidence.egressPrior ? 'Yes' : 'No',
    });
  }
  if (evidence?.unknownPkg !== undefined) {
    contextRows.push({
      k: 'Unknown package',
      v: evidence.unknownPkg ? 'Yes' : 'No',
    });
  }
  if (evidence?.sincePrompt !== undefined) {
    contextRows.push({
      k: 'Calls since prompt',
      v: String(evidence.sincePrompt),
    });
  }
  if (contextRows.length > 0) {
    blocks.push({ kind: 'kv', label: 'Session context', rows: contextRows });
  }

  if (blocks.length === 0) return UNAVAILABLE_NOT_RECORDED;
  return { available: true, blocks };
}

/* -------------------------------------------------------------------------- */
/* Dispatch                                                                   */
/* -------------------------------------------------------------------------- */

/**
 * Resolve the finding-specific detection logic for an alert, or an explicit
 * unavailable result. Never returns type-level copy: a type with no persisted
 * per-finding reason resolves to `{ available: false }` by design.
 */
export function resolveDetectionLogic(
  alert: DetectionLogicInput,
  availability: DetectionLogicAvailability = {}
): DetectionLogicResult {
  const type = alert.type;

  // Ordered most-specific first: `secret-leakage` and the extension weighted
  // path carry structured payloads that outrank any description heuristic.
  if (type === 'secret-leakage')
    return resolveSecretLeakage(alert, availability);
  if (type === 'suspicious_description') {
    return resolveSuspiciousDescription(alert, availability);
  }
  if (type === ExtensionFindingType.ExcessivePermissions) {
    return resolveWeightedExtension(alert);
  }
  if (
    type === 'risky-extension-detected' ||
    type === 'risky-extension-auto-removed'
  ) {
    return resolveRiskyExtension(alert, availability);
  }
  if (type === 'extensions_code_scan') return resolveCodeScan(alert);
  if (type === 'ai-detected-threat') return resolveAiDetectedThreat(alert);
  if (AI_EVIDENCE_TYPES.has(type)) return resolveAiEvidence(alert);
  if (OSV_TYPES.has(type)) return resolveOsv(alert, availability);
  if (MATCHED_PATTERN_TYPES.has(type)) return resolveMatchedPattern(alert);
  if (CONFIG_FLAG_TYPES.has(type)) return resolveConfigFlag(alert);
  if (type === 'tool-call-scan') return resolveToolCallScan(alert);

  // KIR-4703/4704: this is an activity notification, not a rule-based detection —
  // and how Kirin attributes the activity is deliberately NOT surfaced to
  // customers. Keep the copy event-framed and mechanism-free.
  if (type === 'browser-agent-activity') {
    return {
      available: true,
      blocks: [
        {
          kind: 'text',
          label: 'About this activity',
          value:
            'This alert appears only because the site is set to Monitor or Block for AI agents (see the Agents Activity → Browser Agent Sites tab) — it is an expected activity record, not a rule-based threat detection, so there is no per-finding rule to show. Page content is never read.',
        },
      ],
    };
  }

  /*
    KIR-4229: the content around the flagged offsets. A Left-to-Right Mark at
    position 684 says nothing about whether it is obfuscation or a stray mark in
    prose; the excerpt is what makes the finding judgeable. Absent on older
    findings and not backfillable — the scanned body is not retained — so those
    alerts keep today's blocks.
  */
  const surroundingText = trimmed(alert.matchContext);
  const surroundingBlocks: DetectionBlock[] =
    surroundingText != null
      ? [{ kind: 'excerpt', label: 'Surrounding text', value: surroundingText }]
      : [];

  if (type === 'steganography') {
    // The scanner's description is static copy; the only per-finding signal is
    // the decoded payload, which `policies/hidden/index.ts` appends to the
    // location as `Message: "<decoded>"`.
    const decoded = extractQuoted(alert.location, 'Message');
    const blocks: DetectionBlock[] = [
      ...(decoded != null ? [decodedMessageBlock(decoded)] : []),
      ...surroundingBlocks,
    ];
    return blocks.length > 0
      ? { available: true, blocks }
      : UNAVAILABLE_NOT_RECORDED;
  }

  if (CODEPOINT_TYPES.has(type)) {
    const value = trimmed(alert.description);
    const blocks: DetectionBlock[] = [
      ...(value != null
        ? [{ kind: 'code' as const, label: 'Matched character', value }]
        : []),
      ...surroundingBlocks,
    ];
    return blocks.length > 0 ? { available: true, blocks } : UNAVAILABLE_LEGACY;
  }

  if (AUTHORED_REASON_TYPES.has(type)) {
    const value = trimmed(alert.description);
    return value != null
      ? {
          available: true,
          blocks: [{ kind: 'text', label: 'Detection reason', value }],
        }
      : UNAVAILABLE_LEGACY;
  }

  // Known-empty by construction, and anything unrecognised. Both get the
  // honest unavailable state rather than a guess at the description's meaning.
  if (NOT_RECORDED_TYPES.has(type)) return UNAVAILABLE_NOT_RECORDED;
  return UNAVAILABLE_NOT_RECORDED;
}
